import React from "react";
import { Box, Modal } from "@mui/material";

import { StyledButton } from "@/components/button";

import { ModalBody, ModalContent, ModalTitle } from "./shared";

export interface DownloadConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  downloadsUsed: number;
  maxDownloads: number;
  materialName: string;
  isAlreadyDownloaded?: boolean;
}

export const DownloadConfirmationModal: React.FC<DownloadConfirmationModalProps> = ({
  open,
  onClose,
  onConfirm,
  downloadsUsed,
  maxDownloads,
  materialName,
  isAlreadyDownloaded = false,
}) => {
  const remainingDownloads = maxDownloads - downloadsUsed;
  const isLastDownload = remainingDownloads === 1;
  const isLimitReached = downloadsUsed >= maxDownloads && !isAlreadyDownloaded;

  const handleConfirm = () => {
    // Allow download if it's a repeat download or if limit not reached
    if (!isLimitReached || isAlreadyDownloaded) {
      onConfirm();
      onClose();
    }
  };

  return (
    <div>
      <Modal open={open} onClose={onClose}>
        <ModalContent>
          <Box mb={2}>
            <ModalTitle 
              title={isLimitReached && !isAlreadyDownloaded ? "Download Limit erreicht" : "Material Download Bestätigung"} 
              titleColor={isLimitReached && !isAlreadyDownloaded ? "red" : "orange"} 
            />
          </Box>

          <Box mb={2}>
            <ModalBody 
              text={isAlreadyDownloaded 
                ? `"${materialName}" bereits heruntergeladen.`
                : `Du möchtest "${materialName}" herunterladen.`
              } 
            />
          </Box>

          <Box mb={2}>
            <ModalBody 
              text={isAlreadyDownloaded 
                ? `${downloadsUsed}/${maxDownloads} Downloads verwendet.`
                : isLimitReached 
                  ? `Alle ${maxDownloads} Downloads verwendet.`
                  : `${downloadsUsed}/${maxDownloads} Downloads verwendet.${
                      isLastDownload 
                        ? " Letzter Download." 
                        : ` Noch ${remainingDownloads} übrig.`
                    }`
              } 
            />
          </Box>

          {(!isLimitReached || isAlreadyDownloaded) && (
            <Box mb={2}>
              <ModalBody 
                text={isAlreadyDownloaded 
                  ? "Erneut herunterladen?"
                  : "Download fortsetzen?"
                } 
              />
            </Box>
          )}

          <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
            <StyledButton 
              onClick={onClose}
              sx={{ 
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                }
              }}
            >
              {isLimitReached && !isAlreadyDownloaded ? "Verstanden" : "Abbrechen"}
            </StyledButton>
            {(!isLimitReached || isAlreadyDownloaded) && (
              <StyledButton onClick={handleConfirm}>
                {isAlreadyDownloaded ? "Erneut herunterladen" : "Download bestätigen"}
              </StyledButton>
            )}
          </Box>
        </ModalContent>
      </Modal>
    </div>
  );
};
